import { Competence } from "core/blueprint/competencelist/CompetenceListService";

/**
 * Trims a label and collapses runs of whitespace to one space.
 */
export function cleanLabel(value: string): string {
	return value.trim().replace(/\s+/g, " ");
}

/**
 * The equality rule of #635: two labels are the same when their keys are
 * equal, that is after cleaning, stripping accents (NFD, marks removed) and
 * lower-casing. Punctuation stays significant. The twin of the backend's
 * Labels.key.
 */
export function labelKey(value: string): string {
	return cleanLabel(value)
		.normalize("NFD")
		.replace(/\p{M}/gu, "")
		.toLowerCase();
}

/**
 * The distinct labels of a competence tree in document order, first spelling
 * per key wins. This is the vocabulary the editor offers and matches against.
 */
export function collectLabels(competences: Competence[]): string[] {
	const byKey = new Map<string, string>();
	collectInto(competences, byKey);
	return [...byKey.values()];
}

function collectInto(
	competences: Competence[],
	byKey: Map<string, string>
): void {
	for (const competence of competences) {
		for (const label of competence.labels) {
			const cleaned = cleanLabel(label);
			if (cleaned === "") {
				continue;
			}
			const key = labelKey(cleaned);
			if (!byKey.has(key)) {
				byKey.set(key, cleaned);
			}
		}
		collectInto(competence.subcompetences, byKey);
	}
}

/**
 * The values the label field hands back, cleaned, snapped to the first
 * vocabulary spelling with the same key, blanks and repeats dropped. Never
 * mutates its inputs.
 */
export function mergeLabels(values: string[], vocabulary: string[]): string[] {
	const byKey = new Map<string, string>();
	for (const label of vocabulary) {
		const key = labelKey(label);
		if (!byKey.has(key)) {
			byKey.set(key, label);
		}
	}

	const seen = new Set<string>();
	const result: string[] = [];
	for (const value of values) {
		const cleaned = cleanLabel(value);
		if (cleaned === "") {
			continue;
		}
		const key = labelKey(cleaned);
		if (seen.has(key)) {
			continue;
		}
		seen.add(key);
		result.push(byKey.get(key) ?? cleaned);
	}
	return result;
}

/**
 * Shallow equality of two label arrays.
 */
export function sameLabels(a: string[], b: string[]): boolean {
	return a.length === b.length && a.every((label, index) => label === b[index]);
}
